import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { FC } from "react";
import { useTranslations } from "use-intl";

import CasaLogo from "../../../../shared/logo/CasaLogo";
import BrandAndBusinessSelect from "../BrandAndBusinessSelect/BrandAndBusinessSelect";
import InstantSearch from "../InstantSearch/InstantSearch";
import HotkeySettingsButton from "../../../../features/HotkeySettings/components/HotkeySettingsButton";
import HelpButton from "../../../../features/Help/components/HelpButton";
import UserAccount from "../UserAccount/UserAccount";
import styles from "./Navbar.module.scss";
import useNavigationLink from "../../../../hooks/useNavigationLink";
import ChatButton from "../../../../features/Chan/ChatButton";
import { CasaAIProvider } from "../../../../features/CasaAI/context/CasaAI";
import { DraggableChatProvider } from "../../../../features/Chan";
import useAuth from "../../../../hooks/useAuth";
import { shouldShowDueSoonBanner } from "../../../../utils/dueSoonBanner";

interface NavbarProps {
  onToggleSidebar: () => void;
  onToggleChan?: () => void;
  hideSidebar?: boolean;
  hideSearch?: boolean;
  showChan?: boolean;
}

const Navbar: FC<NavbarProps> = ({
  onToggleSidebar,
  hideSidebar = false,
  hideSearch = false,
  showChan = true,
}) => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("xl"));
  const { fullScreenMode } = useNavigationLink();
  const { currentUser } = useAuth();
  const t = useTranslations("invoiceBanner");
  // `count` can genuinely be null (unknown), which must read differently from 0 (due today).
  const dueSoonLead = (count: number | null | undefined) =>
    count == null ? t("dueSoon") : t("dueInDays", { count });
  const overdueLead = (count: number | null | undefined) =>
    count == null ? t("overdue") : t("overdueDays", { count });
  const invoicePaymentStatus = currentUser?.invoicePaymentStatus;
  const showOverdueBanner =
    shouldShowDueSoonBanner(currentUser, invoicePaymentStatus) ||
    invoicePaymentStatus?.state === "grace" ||
    (invoicePaymentStatus?.state === "overdue" &&
      currentUser?._isInternalUser === true);

  return (
    <AppBar
      id={styles.navbar}
      color="transparent"
      position="sticky"
      elevation={0}
      sx={{ bgcolor: "white" }}
    >
      {showOverdueBanner && invoicePaymentStatus?.state === "due_soon" && (
        <Box className={`${styles.banner} ${styles.bannerDueSoon}`}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#B45309"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.bannerIcon}
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5V12l3 2" />
          </svg>
          <Typography variant="body2" component="span" className={styles.bannerText}>
            <strong>{dueSoonLead(invoicePaymentStatus.daysUntilDue)}</strong>
            {invoicePaymentStatus.dueDate && <> {t("dueOn", { date: invoicePaymentStatus.dueDate })}</>}
          </Typography>
        </Box>
      )}
      {showOverdueBanner && invoicePaymentStatus?.state === "grace" && (
        <Box className={`${styles.banner} ${styles.bannerGrace}`}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#C6362B"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.bannerIcon}
          >
            <path d="M10.3 3.2 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0Z" />
            <path d="M12 9v4.5M12 17h.01" />
          </svg>
          <Typography variant="body2" component="span" className={styles.bannerText}>
            <strong>{overdueLead(invoicePaymentStatus.daysOverdue)}</strong>
            {invoicePaymentStatus.graceUntil && (
              <> {t("graceEnds", { date: invoicePaymentStatus.graceUntil })}</>
            )}
          </Typography>
        </Box>
      )}
      {showOverdueBanner && invoicePaymentStatus?.state === "overdue" && (
        <Box className={`${styles.banner} ${styles.bannerRestricted}`}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EE7B72"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.bannerIcon}
          >
            <path d="M12 3 4.5 6v5c0 4.6 3.2 7.9 7.5 9 4.3-1.1 7.5-4.4 7.5-9V6L12 3Z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          <Typography variant="body2" component="span" className={styles.bannerText}>
            <strong>{t("restricted")}</strong>{" "}
            {t("restrictedDetail", { tenant: currentUser?.tenant?.displayName ?? "" })}
          </Typography>
          <span className={styles.bannerBadge}>{t("internal")}</span>
        </Box>
      )}
      <Toolbar className={styles.navbarContainer}>
        <Box className={styles.navLeft}>
          {!hideSidebar && (isMobileView || fullScreenMode) && (
            <IconButton onClick={onToggleSidebar}>
              <MenuIcon />
            </IconButton>
          )}
          <CasaLogo />
        </Box>
        <Box className={styles.navRight}>
          {showChan && (
            <CasaAIProvider>
              <DraggableChatProvider>
                <Box className={styles.mobileHidden}>
                  <ChatButton />
                </Box>
              </DraggableChatProvider>
            </CasaAIProvider>
          )}
          {!hideSearch && (
            <>
              <Box className={styles.mobileHidden}>
                <InstantSearch />
              </Box>
              <Box className={styles.mobileHidden}>
                <HotkeySettingsButton />
              </Box>
              <Box className={styles.mobileHidden}>
                <HelpButton />
              </Box>
            </>
          )}
          <BrandAndBusinessSelect />
          <UserAccount />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
